import React from 'react'
import PropTypes from 'prop-types'
import {AspectRatio, Box, Button, Flex, Img, Link, Text, useStyleConfig} from '@chakra-ui/react'
import {remCalc} from '../../../../theme/_utils'
import useDamImage from '../../../../custom-hooks/use-dam-image'
import {useGetCategoriesData} from '../../../../custom-api/hooks/use-get-categories-data'
import {getGlobalLink} from '../../../../utils/contentstack-utils'

export const ContentSliderTile = ({
    isDesktop,
    image = null,
    textblock = null,
    textVariant,
    ctaVariant,
}) => {
    const styles = useStyleConfig('ContentSlider')
    const damImage = useDamImage(image)
    const textColor = isDesktop ? 'text.inverse' : textVariant
    const ctaColor = isDesktop ? 'inverse-primary' : ctaVariant
    const categoryData = useGetCategoriesData()
    const {url, label, isAbsolute} = getGlobalLink(textblock?.button, categoryData)
    const buttonProps = isAbsolute
        ? {
              as: 'a',
              href: url,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: url || '',
          }

    return isDesktop ? (
        <Box data-testid="content-slider-tile-desktop">
            {damImage.url && (
                <AspectRatio ratio={damImage.ratio} {...styles.image}>
                    <Img src={damImage.url} alt={image?.alt || ''} color={textVariant} />
                </AspectRatio>
            )}
            <Flex
                {...styles.textblock}
                direction="column"
                px={`min(12%, ${remCalc(219)})`}
                backgroundColor="rgba(0, 0, 0, 0.3)"
                position="absolute"
                gap={remCalc(40)}
            >
                <Flex direction="column" gap={remCalc(24)}>
                    {textblock?.label_paragraph && (
                        <Text
                            textStyle="body-xxsmall"
                            fontWeight={'bold'}
                            color={textColor}
                            {...styles.label_paragraph}
                        >
                            {textblock?.label_paragraph}
                        </Text>
                    )}
                    {textblock?.title && (
                        <Text textStyle="display-large" fontWeight={'medium'} color={textColor}>
                            {textblock?.title}
                        </Text>
                    )}
                </Flex>
                {textblock?.body_copy && (
                    <Box
                        textStyle="body-xlarge"
                        fontWeight={'normal'}
                        color={textColor}
                        dangerouslySetInnerHTML={{
                            __html: textblock?.body_copy,
                        }}
                    />
                )}
                {label && url && (
                    <Button {...buttonProps} {...styles.cta} variant={ctaColor}>
                        {label}
                    </Button>
                )}
            </Flex>
        </Box>
    ) : (
        <Flex data-testid="content-slider-tile-mobile" direction="column" gap={remCalc(24)}>
            {damImage.url && (
                <AspectRatio ratio={damImage.ratio} {...styles.image}>
                    <Img src={damImage.url} alt={image?.alt || ''} color={textVariant} />
                </AspectRatio>
            )}
            <Flex direction="column" gap={remCalc(24)} alignItems={'center'}>
                {textblock?.label_paragraph && (
                    <Text
                        textStyle="body-xxsmall"
                        fontWeight={'bold'}
                        color={textColor}
                        {...styles.label_paragraph}
                    >
                        {textblock?.label_paragraph}
                    </Text>
                )}
                {textblock?.title && (
                    <Text textStyle="display-large" fontWeight={'medium'} color={textColor}>
                        {textblock?.title}
                    </Text>
                )}
                {textblock?.body_copy && (
                    <Box
                        textStyle="body-large"
                        fontWeight={'normal'}
                        color={textColor}
                        dangerouslySetInnerHTML={{
                            __html: textblock?.body_copy,
                        }}
                    />
                )}
                {label && url && (
                    <Button {...buttonProps} {...styles.cta} variant={ctaColor}>
                        {label}
                    </Button>
                )}
            </Flex>
        </Flex>
    )
}

ContentSliderTile.propTypes = {
    isDesktop: PropTypes.bool,
    isMobile: PropTypes.bool,
    image: PropTypes.shape(),
    textblock: PropTypes.shape({
        label: PropTypes.string,
        label_paragraph: PropTypes.string,
        title: PropTypes.string,
        body_copy: PropTypes.string,
        button: PropTypes.shape(),
    }),
    textVariant: PropTypes.string,
    ctaVariant: PropTypes.string,
}
